<template>
  <div class="sender-policy-panel">
    <div class="box-title">
      <h4>
        <i class="fal fa-user-tag"></i>
        ຜູ້ສົ່ງປະຈຳສາຂາ ແລະ ບລັອກເບີຜູ້ສົ່ງ (Branch Sender)
      </h4>
    </div>
    <p class="sender-policy-hint">
      ສອງການຕັ້ງຄ່ານີ້ມີຜົນ <b>ສະເພາະເມື່ອສາຂານີ້ເປັນສາຂາຕົ້ນທາງ</b> ຂອງບິນ ຄື ສາຂານີ້ສ້າງບິນເອງ
      ຫຼື ຮັບບິນ pre-order ຂອງລູກຄ້າເຂົ້າ — ສາຂາອື່ນບໍ່ໄດ້ຮັບຜົນກະທົບ.
    </p>

    <div v-if="loadError" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-exclamation-triangle" :style="{ marginRight: '6px' }"></i>
      ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ ຫຼື ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ພາຍຫຼັງ.
      <vs-button transparent :style="{ display: 'inline-block', marginLeft: '8px' }" @click="retryLoad()">
        ລອງໃໝ່
      </vs-button>
    </div>

    <div v-else-if="!schemaReady" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-database" :style="{ marginRight: '6px' }"></i>
      ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ) —
      ການສ້າງບິນ ແລະ ການຮັບບິນເຂົ້າຍັງເຮັດວຽກຄືເກົ່າທຸກຢ່າງ.
    </div>

    <!-- ບັນທຶກລົງຖານຂໍ້ມູນແລ້ວ ແຕ່ລ້າງ cache ບໍ່ໄດ້ — ການສ້າງບິນອາດຍັງໃຊ້ຄ່າເກົ່າ
         ຈົນກວ່າ cache ຈະໝົດອາຍຸ (ບໍ່ເກີນ 5 ນາທີ). -->
    <CacheStaleNotice :visible="cacheStale">ການສ້າງ/ຮັບບິນອາດຍັງໃຊ້ຄ່າເກົ່າ</CacheStaleNotice>

    <!-- ══════════════════ 1. ຜູ້ສົ່ງປະຈຳສາຂາ (fixed sender) ══════════════════ -->
    <div class="sender-policy-section">
      <h5 class="sender-policy-section-title">
        <i class="fal fa-user-check" :style="{ marginRight: '6px' }"></i>
        1. ຜູ້ສົ່ງປະຈຳສາຂາ (Fixed Sender)
      </h5>
      <p class="sender-policy-hint">
        ຖ້າຕັ້ງເບີໄວ້ ທຸກບິນທີ່ສາຂານີ້ສ້າງຈະຖືກບັນທຶກເປັນ "ຜູ້ສົ່ງ" ຕາມເບີນີ້ ແທນເບີທີ່ພະນັກງານປ້ອນ.
        ສຳລັບບິນ pre-order ທີ່ສາຂານີ້ຮັບເຂົ້າ ຈະປ່ຽນໃຫ້<b>ສະເພາະ</b>ກໍລະນີຜູ້ສົ່ງເດີມຍັງບໍ່ແມ່ນລູກຄ້າທີ່ລົງທະບຽນ
        (ລູກຄ້າທີ່ມີບັນຊີແອັບຈະຖືກຮັກສາໄວ້ ເພື່ອບໍ່ໃຫ້ເສຍບິນ ແລະ ເງິນ COD ຂອງເຂົາ).
        ຖ້າບໍ່ຕັ້ງ ລະບົບເຮັດວຽກຄືເກົ່າ.
      </p>

      <div class="columns is-multiline" :style="{ marginTop: '6px' }">
        <div class="column is-5">
          <div class="sender-policy-card" :class="{ 'is-enabled': hasActiveFixedSender }">
            <label class="label">ຜູ້ສົ່ງປະຈຳສາຂາ ປັດຈຸບັນ</label>
            <p class="sender-policy-value">
              <i :class="hasActiveFixedSender ? 'fal fa-check-circle' : 'fal fa-times-circle'"
                 :style="{ marginRight: '6px' }"></i>
              {{ hasActiveFixedSender ? info.fixed_sender.tel : 'ບໍ່ໄດ້ຕັ້ງ (ໃຊ້ຜູ້ສົ່ງຕາມປົກກະຕິ)' }}
            </p>
            <p v-if="hasActiveFixedSender" class="sender-policy-note">
              ຊື່ຜູ້ສົ່ງ: {{ fixedSenderDisplayName }}
            </p>
            <p v-else-if="hasInactiveFixedSender" class="sender-policy-note is-muted">
              ເຄີຍຕັ້ງເປັນ {{ info.fixed_sender.tel }} (ຍົກເລີກແລ້ວ)
            </p>
            <p v-if="hasActiveFixedSender && !info.fixed_sender.cod_registered"
               class="sender-policy-warning">
              <i class="fal fa-exclamation-triangle" :style="{ marginRight: '4px' }"></i>
              ເບີນີ້ຍັງບໍ່ໄດ້ລົງທະບຽນ COD — ບິນ COD ທີ່ສ້າງຢູ່ສາຂານີ້ຈະສ້າງບໍ່ໄດ້
              (ERROR_NOT_REGISTER_COD). ກະລຸນາລົງທະບຽນ COD ໃຫ້ເບີນີ້ກ່ອນ.
            </p>
            <div class="sender-policy-toggle" v-if="hasActiveFixedSender">
              <vs-button danger flat :loading="removeFixedLoading" :disabled="!canMutateFixed"
                         @click="removeFixedModalActive = true">
                <i class="fad fa-trash-alt" :style="{ marginRight: '6px' }"></i>
                ຍົກເລີກຜູ້ສົ່ງປະຈຳສາຂາ
              </vs-button>
            </div>
          </div>
        </div>
      </div>

      <!-- The edit form: both inputs on one row, the save button on its own row below
           them (the name field carries a help note, so a button in the same row would
           sink to the note's baseline). -->
      <div class="sender-policy-form">
        <p class="sender-policy-form-title">
          <i class="fal fa-edit" :style="{ marginRight: '6px' }"></i>
          ຕັ້ງ ຫຼື ປ່ຽນຜູ້ສົ່ງປະຈຳສາຂາ
        </p>
        <div class="columns is-multiline">
          <div class="column is-4">
            <div class="field">
              <div class="control">
                <label class="label">
                  ເບີຜູ້ສົ່ງປະຈຳສາຂາ
                  <span class="text-danger">{{ server_errors.fixedTel }}</span>
                </label>
                <input class="input" name="fixedTel" placeholder="ຕົວຢ່າງ: 55538210"
                       v-model="fixedTel"/>
              </div>
            </div>
          </div>
          <div class="column is-5">
            <div class="field">
              <div class="control">
                <label class="label">ຊື່ຜູ້ສົ່ງ (ບໍ່ບັງຄັບ)</label>
                <input class="input" name="fixedName" :placeholder="info.branch_name || 'ຊື່ສາຂາ'"
                       v-model="fixedName"/>
                <p class="sender-policy-field-note">
                  ໃຊ້ສະເພາະກໍລະນີເບີນີ້ຍັງບໍ່ມີຂໍ້ມູນລູກຄ້າໃນລະບົບ — ຖ້າມີແລ້ວ ລະບົບໃຊ້ຊື່ເດີມຂອງລູກຄ້າ
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="sender-policy-form-actions">
          <vs-button :loading="saveFixedLoading" :disabled="!fixedTel || !canMutateFixed"
                     @click="SaveFixedSender()">
            <i class="fal fa-check-circle" :style="{ marginRight: '6px' }"></i>
            ບັນທຶກຜູ້ສົ່ງປະຈຳສາຂາ
          </vs-button>
        </div>
      </div>
    </div>

    <!-- ══════════════════ 2. ບລັອກເບີຜູ້ສົ່ງ (blocked sender phones) ══════════════════ -->
    <div class="sender-policy-section">
      <h5 class="sender-policy-section-title">
        <i class="fal fa-user-slash" :style="{ marginRight: '6px' }"></i>
        2. ບລັອກເບີຜູ້ສົ່ງ ສະເພາະສາຂານີ້ (Blocked Senders)
      </h5>
      <p class="sender-policy-hint">
        ເບີທີ່ຢູ່ໃນລາຍການນີ້ ຈະສ້າງບິນຢູ່ສາຂານີ້ບໍ່ໄດ້ ແລະ ສາຂານີ້ຮັບບິນ pre-order ຂອງເບີນີ້ເຂົ້າບໍ່ໄດ້.
        ເບີດຽວກັນນີ້ <b>ຍັງໃຊ້ງານໄດ້ປົກກະຕິຢູ່ສາຂາອື່ນ</b>.
        ຂໍ້ຄວາມແຈ້ງເຕືອນໃຫ້ພະນັກງານເປັນແບບທົ່ວໄປ (ບໍ່ບອກວ່າເບີຖືກບລັອກ).
      </p>

      <div class="sender-policy-form">
        <p class="sender-policy-form-title">
          <i class="fal fa-plus" :style="{ marginRight: '6px' }"></i>
          ເພີ່ມເບີຜູ້ສົ່ງທີ່ຕ້ອງການບລັອກ
        </p>
        <div class="columns is-multiline">
          <div class="column is-4">
            <div class="field">
              <div class="control">
                <label class="label">
                  ເບີຜູ້ສົ່ງທີ່ຕ້ອງການບລັອກ
                  <span class="text-danger">{{ server_errors.blockedTel }}</span>
                </label>
                <input class="input" name="blockedTel" placeholder="ຕົວຢ່າງ: 55512345"
                       v-model="blockedTel"/>
              </div>
            </div>
          </div>
          <div class="column is-5">
            <div class="field">
              <div class="control">
                <label class="label">ເຫດຜົນ (ບໍ່ບັງຄັບ)</label>
                <input class="input" name="blockedNote" placeholder="ເຊັ່ນ: ສາຂາຂໍບລັອກ ວັນທີ..."
                       v-model="blockedNote"/>
              </div>
            </div>
          </div>
        </div>
        <div class="sender-policy-form-actions">
          <vs-button :loading="addBlockedLoading" :disabled="!blockedTel || !canMutateBlocked"
                     @click="AddBlockedTel()">
            <i class="fal fa-plus-circle" :style="{ marginRight: '6px' }"></i>
            ເພີ່ມເບີບລັອກ
          </vs-button>
        </div>
      </div>

      <div class="center" :style="{ marginTop: '10px' }">
        <vs-table>
          <template #thead>
            <vs-tr>
              <vs-th>ລຳດັບ</vs-th>
              <vs-th>ເບີຜູ້ສົ່ງ</vs-th>
              <vs-th>ເຫດຜົນ</vs-th>
              <vs-th>ສະຖານະ</vs-th>
              <vs-th>ອັບເດດຫຼ້າສຸດ</vs-th>
              <vs-th></vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <vs-tr :key="index" v-for="(item, index) in info.blocked_tels">
              <vs-td>{{ index + 1 }}</vs-td>
              <vs-td>{{ item.tel }}</vs-td>
              <vs-td>{{ item.note || '-' }}</vs-td>
              <vs-td>
                <span :class="item.is_active ? 'tag-active' : 'tag-inactive'">
                  {{ item.is_active ? 'ບລັອກຢູ່' : 'ຍົກເລີກແລ້ວ' }}
                </span>
              </vs-td>
              <vs-td>{{ item.updated_at || '-' }}</vs-td>
              <vs-td>
                <vs-button v-if="item.is_active && canMutateBlocked" circle icon flat danger
                           @click="OpenRemoveBlocked(item)">
                  <i class="fad fa-trash-alt"></i>
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
        <p v-if="(info.blocked_tels || []).length === 0 && !isLoading" class="sender-policy-empty">
          ຍັງບໍ່ມີເບີຜູ້ສົ່ງທີ່ຖືກບລັອກຢູ່ສາຂານີ້
        </p>
      </div>
    </div>

    <Loading v-if="isLoading"/>

    <vs-dialog prevent-close v-model="removeFixedModalActive">
      <div class="con-content" :style="{ marginTop: '15px' }">
        <p :style="{ fontSize: '18px', color: '#195BFF' }">
          ກະລຸນາກົດຢືນຢັນ ເພື່ອຍົກເລີກຜູ້ສົ່ງປະຈຳສາຂາ — ບິນໃໝ່ຈະໃຊ້ຜູ້ສົ່ງຕາມທີ່ພະນັກງານປ້ອນຄືເກົ່າ
        </p>
      </div>
      <div class="con-footer" :style="{ marginTop: '25px' }">
        <vs-button :loading="removeFixedLoading" transparent :style="{ color: 'crimson' }"
                   @click="RemoveFixedSender()">
          ຢືນຢັນ
        </vs-button>
        <vs-button @click="removeFixedModalActive = false" dark transparent>ຍົກເລີກ</vs-button>
      </div>
    </vs-dialog>

    <vs-dialog prevent-close v-model="removeBlockedModalActive">
      <div class="con-content" :style="{ marginTop: '15px' }">
        <p :style="{ fontSize: '18px', color: '#195BFF' }">
          ກະລຸນາກົດຢືນຢັນ ເພື່ອຍົກເລີກການບລັອກເບີ
          <b>{{ removingBlocked ? removingBlocked.tel : '' }}</b>
        </p>
      </div>
      <div class="con-footer" :style="{ marginTop: '25px' }">
        <vs-button :loading="removeBlockedLoading" transparent :style="{ color: 'crimson' }"
                   @click="RemoveBlockedTel()">
          ຢືນຢັນ
        </vs-button>
        <vs-button @click="removeBlockedModalActive = false" dark transparent>ຍົກເລີກ</vs-button>
      </div>
    </vs-dialog>
  </div>
</template>
<script>
import Loading from '@/views/Loading';
import CacheStaleNotice from '@/components/CacheStaleNotice';

export default {
  components: { Loading, CacheStaleNotice },

  data: () => ({
    isLoading: false,
    loadError: false,
    /// Last mutation committed but its cache invalidation failed — creates/receives can
    /// lag behind the saved configuration until the backend TTL expires.
    cacheStale: false,
    saveFixedLoading: false,
    removeFixedLoading: false,
    removeFixedModalActive: false,
    addBlockedLoading: false,
    removeBlockedLoading: false,
    removeBlockedModalActive: false,
    removingBlocked: null,
    fixedTel: '',
    fixedName: '',
    blockedTel: '',
    blockedNote: '',
    info: {
      branch_id: null,
      branch_name: '',
      fixed_sender_schema_ready: true,
      blocked_schema_ready: true,
      fixed_sender: null,
      blocked_tels: [],
    },
    server_errors: {
      fixedTel: '',
      blockedTel: '',
    },
  }),

  computed: {
    branchId() {
      return this.$route.params.id;
    },
    hasActiveFixedSender() {
      return !!(this.info.fixed_sender && this.info.fixed_sender.is_active);
    },
    hasInactiveFixedSender() {
      return !!(this.info.fixed_sender && !this.info.fixed_sender.is_active);
    },
    // The customer row the phone actually resolves to wins over the configured name:
    // that is the name the bill will carry.
    fixedSenderDisplayName() {
      const fixed = this.info.fixed_sender || {};
      if (fixed.customer && fixed.customer.name) {
        return fixed.customer.name;
      }
      return fixed.name || this.info.branch_name || '-';
    },
    schemaReady() {
      return this.info.fixed_sender_schema_ready !== false
          && this.info.blocked_schema_ready !== false;
    },
    // Each half needs its own table in place; a half-migrated backend still serves
    // the half that exists.
    canMutateFixed() {
      return !this.loadError && this.info.fixed_sender_schema_ready !== false;
    },
    canMutateBlocked() {
      return !this.loadError && this.info.blocked_schema_ready !== false;
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    retryLoad() {
      this.loadError = false;
      this.fetchData();
    },
    fetchData() {
      this.isLoading = true;
      /// A fresh load of the current server state is where a warning about a PAST save
      /// stops being useful — by then the cache has usually expired, and the page below
      /// shows the value actually in force.
      this.cacheStale = false;
      this.$axios.get('get-branch-sender-policy/' + this.branchId).then(res => {
        this.applyInfo(res.data);
        this.isLoading = false;
      }).catch(() => {
        // Backend not deployed yet / outage: say so instead of rendering empty values
        // as if the branch were simply unconfigured.
        this.isLoading = false;
        this.loadError = true;
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      });
    },
    applyInfo(data) {
      this.info = data;
      this.cacheStale = data.cache_stale === true;
      // Prefill the form with what is in force, so an edit starts from the real value.
      const fixed = data.fixed_sender;
      if (fixed && fixed.is_active) {
        this.fixedTel = fixed.tel;
        this.fixedName = fixed.name || '';
      } else {
        this.fixedTel = '';
        this.fixedName = '';
      }
    },
    SaveFixedSender() {
      if (!this.fixedTel) {
        return;
      }
      this.server_errors.fixedTel = '';
      this.saveFixedLoading = true;
      this.$axios.post('set-branch-fixed-sender/' + this.branchId, {
        tel: this.fixedTel,
        name: this.fixedName || null,
      }).then(res => {
        this.saveFixedLoading = false;
        if (res.data.success == true) {
          this.applyInfo(res.data);
          this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
        }
      }).catch(error => {
        this.saveFixedLoading = false;
        if (error.response && error.response.status === 422) {
          this.server_errors.fixedTel = this.serverErrorMessage(error.response.data?.error);
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    RemoveFixedSender() {
      this.removeFixedLoading = true;
      this.$axios.delete('delete-branch-fixed-sender/' + this.branchId).then(res => {
        this.removeFixedLoading = false;
        this.removeFixedModalActive = false;
        this.applyInfo(res.data);
        if (res.data.success == true) {
          this.$notification.OpenNotification_DeleteItem_OnSuccess('top-right', 'danger', 3000);
        } else {
          // e.g. NOT_FOUND (already removed elsewhere) — show the refreshed state
          // instead of pretending the delete just succeeded.
          this.$notification.openNotification(
            { title: 'ຜູ້ສົ່ງປະຈຳສາຂາ', text: 'ບໍ່ມີລາຍການໃຫ້ຍົກເລີກແລ້ວ — ໂຫຼດຂໍ້ມູນຄືນໃໝ່' },
            'top-right', 'danger', 5000
          );
        }
      }).catch(error => {
        this.removeFixedLoading = false;
        this.removeFixedModalActive = false;
        this.notifyError('ຜູ້ສົ່ງປະຈຳສາຂາ', error);
      });
    },
    AddBlockedTel() {
      if (!this.blockedTel) {
        return;
      }
      this.server_errors.blockedTel = '';
      this.addBlockedLoading = true;
      this.$axios.post('add-branch-blocked-sender-tel/' + this.branchId, {
        tel: this.blockedTel,
        note: this.blockedNote || null,
      }).then(res => {
        this.addBlockedLoading = false;
        if (res.data.success == true) {
          this.applyInfo(res.data);
          this.blockedTel = '';
          this.blockedNote = '';
          this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        }
      }).catch(error => {
        this.addBlockedLoading = false;
        if (error.response && error.response.status === 422) {
          this.server_errors.blockedTel = this.serverErrorMessage(error.response.data?.error);
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    OpenRemoveBlocked(item) {
      this.removingBlocked = item;
      this.removeBlockedModalActive = true;
    },
    RemoveBlockedTel() {
      if (!this.removingBlocked) {
        return;
      }
      const id = this.removingBlocked.id;
      this.removeBlockedLoading = true;
      this.$axios.delete('delete-branch-blocked-sender-tel/' + this.branchId + '/' + id).then(res => {
        this.removeBlockedLoading = false;
        this.removeBlockedModalActive = false;
        this.removingBlocked = null;
        this.applyInfo(res.data);
        if (res.data.success == true) {
          this.$notification.OpenNotification_DeleteItem_OnSuccess('top-right', 'danger', 3000);
        } else {
          this.$notification.openNotification(
            { title: 'ບລັອກເບີຜູ້ສົ່ງ', text: 'ບໍ່ມີລາຍການໃຫ້ຍົກເລີກແລ້ວ — ໂຫຼດຂໍ້ມູນຄືນໃໝ່' },
            'top-right', 'danger', 5000
          );
        }
      }).catch(error => {
        this.removeBlockedLoading = false;
        this.removeBlockedModalActive = false;
        this.removingBlocked = null;
        this.notifyError('ບລັອກເບີຜູ້ສົ່ງ', error);
      });
    },
    notifyError(title, error) {
      if (error.response && error.response.status === 422) {
        this.$notification.openNotification(
          { title: title, text: this.serverErrorMessage(error.response.data?.error) },
          'top-right', 'danger', 5000
        );
      } else {
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      }
    },
    serverErrorMessage(errorCode) {
      if (errorCode === 'SCHEMA_NOT_READY') {
        return 'ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ)';
      }
      if (errorCode === 'BRANCH_NOT_FOUND') {
        return 'ບໍ່ພົບສາຂານີ້ໃນລະບົບ';
      }
      if (errorCode === 'TEL_REQUIRED') {
        return 'ກະລຸນາປ້ອນເບີໂທ';
      }
      if (errorCode === 'TEL_INVALID') {
        return 'ເບີໂທບໍ່ຖືກຕ້ອງ ຫຼື ຍາວເກີນໄປ';
      }
      if (errorCode === 'TEL_IS_BLOCKED_AT_THIS_BRANCH') {
        return 'ເບີນີ້ຢູ່ໃນລາຍການບລັອກຂອງສາຂານີ້ຢູ່ແລ້ວ — ຕ້ອງຍົກເລີກການບລັອກກ່ອນ';
      }
      if (errorCode === 'TEL_IS_FIXED_SENDER') {
        return 'ເບີນີ້ແມ່ນຜູ້ສົ່ງປະຈຳສາຂານີ້ — ບລັອກບໍ່ໄດ້ ເພາະຈະເຮັດໃຫ້ສ້າງບິນບໍ່ໄດ້ທັງໝົດ';
      }
      return 'ບໍ່ສາມາດບັນທຶກໄດ້ ກະລຸນາກວດຂໍ້ມູນທີ່ປ້ອນຄືນ';
    },
  },
};
</script>
<style lang="scss" scoped>
.sender-policy-panel {
  margin-top: 10px;

  .sender-policy-hint {
    color: #666;
    font-size: 14px;
    margin-top: 6px;
  }

  .sender-policy-section {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid #e0e0e0;

    .sender-policy-section-title {
      font-size: 16px;
      font-weight: 600;
      color: #195BFF;
    }
  }

  .sender-policy-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px;

    &.is-enabled {
      border-color: #195BFF;
    }

    .sender-policy-value {
      font-size: 20px;
      font-weight: 600;
      margin-top: 4px;
    }

    .sender-policy-note {
      margin-top: 6px;
      font-size: 13px;
      color: #666;

      &.is-muted {
        color: #999;
      }
    }

    .sender-policy-warning {
      margin-top: 8px;
      font-size: 13px;
      color: #d97706;
    }

    .sender-policy-toggle {
      display: flex;
      justify-content: center;
      margin-top: 10px;
    }
  }

  .sender-policy-field-note {
    margin-top: 4px;
    font-size: 12px;
    color: #999;
  }

  // The edit form under each section's status card: a quiet box so the inputs read as
  // one unit, with the submit button on its own row under them (never beside them —
  // a help note under a field would otherwise push the button down to its baseline).
  .sender-policy-form {
    margin-top: 14px;
    padding: 14px 16px 12px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: #fafbfc;

    .sender-policy-form-title {
      font-size: 14px;
      font-weight: 600;
      color: #444;
      margin-bottom: 2px;
      // The edit page centres its headings; the form title belongs with the
      // left-aligned inputs under it.
      text-align: left;
    }

    // Bulma's gap under the inputs row — the button row below carries its own spacing.
    .columns:not(:last-child) {
      margin-bottom: 0;
    }

    .sender-policy-form-actions {
      display: flex;
      align-items: center;
      margin-top: 2px;

      // Vuesax gives every button a 5px margin; drop the left one so the button's
      // edge lines up with the inputs above it.
      .vs-button {
        margin-left: 0;
        margin-right: 8px;
      }
    }
  }

  .sender-policy-empty {
    color: #999;
    font-size: 14px;
    padding: 12px 0;
    text-align: center;
  }

  .tag-active {
    color: crimson;
    font-weight: 600;
  }

  .tag-inactive {
    color: #999;
  }
}
</style>
